/**
 * Operations › Deals, after Foundations (which lives in ../content.ts): one section per
 * skill, in the order a deal is lived (setup, fundraise, closing, post-close, special
 * cases). `group` turns the page into a tree in the sidebar and the TOC. Facts come from
 * hercules_* and poseidon_*; only write what the code does. Setup sections also explain
 * the financial why behind each field, for readers who don't know private markets.
 */
import type { UniversitySection } from "../content";

export const DEALS_SECTIONS: UniversitySection[] = [
  // ---- Setup ----
  {
    id: "setup-product-type",
    group: "Setup",
    title: "Product Type",
    blocks: [
      {
        type: "paragraph",
        text: "The first choice when creating a deal, and the one that shapes its economics. It answers two questions: does the vehicle buy one asset or several, and does the money come in all at once or in tranches? It sets the deal subtype, and the type follows from it: Fund → fund, anything else → spv.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Standard SPV",
            description:
              'One investment into a US startup (a corporation), raised and closed in one go. Investors pool their money, the SPV wires it to the company, and the SPV holds the shares on everyone\'s behalf. It is the simplest and cheapest product, and the right fit for the classic "I have an allocation in a priced round" deal.',
          },
          {
            term: "Premium SPV",
            description:
              "Still one vehicle per deal, but for anything outside that simple case: a secondary purchase of existing shares, an SPV investing into another SPV or a fund, crypto, a public company, an acquisition, or a raise that closes in several tranches. These deals need more legal and accounting work, which is why they cost more.",
          },
          {
            term: "Fund",
            description:
              "One entity that invests in several assets over time. Investors commit to the manager's strategy rather than to a single company, so their money is spread across the portfolio and their return is the result of all its investments together.",
          },
        ],
      },
      {
        type: "heading",
        text: "Why the number of closes matters",
      },
      {
        type: "paragraph",
        text: "A close is the moment money raised so far is released to the asset. Some managers need to close part of the round early (the company wants the money now) and keep raising afterwards. Every extra close means another round of reconciliation, fees, wires and paperwork. Only Premium SPVs and Funds support additional closes. A Standard SPV that needs another close is charged as a Premium SPV plus $2,000, so pick Premium up front when you already know the raise will be staged.",
      },
    ],
  },
  {
    id: "setup-account",
    group: "Setup",
    title: "Account",
    blocks: [
      {
        type: "paragraph",
        text: "The account (organization) is the fund manager's brand: the name investors recognise from one deal to the next. Pick an existing account or create one. It does not hold any money and is not a legal entity; each deal still has its own entity and bank account, so the money of one deal is never mixed with another.",
      },
      {
        type: "paragraph",
        text: "Grouping deals under one account is what lets a manager build a track record: their deals, the amounts raised and the investors who came back can all be read together.",
      },
      {
        type: "list",
        items: [
          "Required to submit the deal.",
          "Gives access to the deal to every member of the account, so a manager's team can follow the raise without being added deal by deal.",
          "A new deal inherits the account's legal template when none is set, which keeps the terms consistent across a manager's deals.",
          "An account can't be archived while one of its deals is still raising (draft, submitted, onboarding, closing, close-requested).",
        ],
      },
    ],
  },
  {
    id: "setup-deal-info",
    group: "Setup",
    title: "Deal Info",
    blocks: [
      {
        type: "paragraph",
        text: "The basic terms investors see before they commit. Together they tell an investor how big the round is, how much they need to bring and how long they have to decide.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Target raise amount",
            description:
              "How much the manager wants to collect from investors. It drives the raise progress bar, so it is the number investors use to judge momentum. It should be the size of the allocation the manager has secured, not a wish: raising more than the company will accept means refunding the difference.",
          },
          {
            term: "Minimum investment",
            description:
              "The smallest ticket the manager wants to accept. It defaults to $5,000 and pre-fills the invest form and the invite amount. It is a default, not a hard floor: the form only requires at least $1. A higher minimum keeps the investor count down, which matters because the number of investors in a vehicle is legally capped (see Investor type below).",
          },
          {
            term: "Estimated closing date",
            description:
              "When the manager expects to wire the money to the asset, usually driven by the company's own round deadline. It also sets the default signing and wiring deadlines on invites (closing date minus 2 business days), giving the team time to reconcile wires before the close.",
          },
          {
            term: "Offering type",
            description:
              "506(b) (default) or 506(c): whether the deal can be advertised. See Offering type.",
          },
          {
            term: "Investor type",
            description:
              "Accredited Investors (3(c)(1), the default) or Qualified Purchasers (3(c)(7)). A 3(c)(1) vehicle is limited in how many investors it can have (our fund documents cap it at 99). A 3(c)(7) vehicle can take many more, but only investors wealthy enough to qualify as qualified purchasers (for individuals, at least $5M in investments).",
          },
          {
            term: "Financial statements",
            description:
              "None, Quarterly or Annually: how often investors receive financial reporting on the vehicle. More frequent reporting is more work for the manager and for us.",
          },
          {
            term: "Deal memo",
            description:
              "The manager's investment case, shown on the investor overview: what the company does, why now, the round terms (valuation, lead investor) and the risks. It is the main thing investors read before committing.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "Target raise, minimum and closing date are not required to submit, but a deal without them is much harder for investors to evaluate.",
      },
    ],
  },
  {
    id: "setup-offering-type",
    group: "Setup",
    title: "Offering type (506(b) vs 506(c))",
    blocks: [
      {
        type: "paragraph",
        text: "Private deals are not registered with the SEC like a public stock. Instead they rely on an exemption, almost always Regulation D Rule 506. The rule comes in two flavours, and the choice decides how the manager is allowed to find investors.",
      },
      {
        type: "table",
        columns: ["", "506(b)", "506(c)"],
        rows: [
          [
            "Advertising",
            "Not allowed: only people the manager already has a relationship with",
            "Allowed: social media, newsletters, public posts",
          ],
          [
            "Accreditation",
            "Investors self-certify they are accredited",
            "The manager must take reasonable steps to verify it",
          ],
          [
            "Typical fit",
            "A manager raising from their own network",
            "A manager with an audience who wants to market the deal",
          ],
        ],
      },
      {
        type: "list",
        items: [
          "506(b) is the default.",
          "On a 506(c) deal, investors provide proof of accreditation, such as a verification letter or financial documents. Missing accreditation verification documents show as a warning on the identity; they do not block the investment.",
        ],
      },
      {
        type: "callout",
        tone: "warning",
        title: "Advertising a 506(b) deal",
        text: "Once a 506(b) deal has been publicly promoted, it can no longer rely on 506(b). If a manager wants to post about the deal, it must be set up as 506(c) from the start.",
      },
      {
        type: "paragraph",
        text: "Every investor identity needs at least one accreditation to invest, whatever the offering type. In Poseidon, deals can be counted and listed by offering type.",
      },
    ],
  },
  {
    id: "setup-management-fees",
    group: "Setup",
    title: "Management Fees",
    blocks: [
      {
        type: "paragraph",
        text: "The fee the fund manager earns for finding, running and reporting on the deal, as a percentage of each investor's capital. Unlike carry, investors pay it whether or not the investment works out, so it is the part of the manager's pay that does not depend on performance.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Percentage",
            description:
              "Commonly between 0% and 2% on SPVs. 0 is a valid choice and still counts as filled in: many managers waive the fee and earn only carry.",
          },
          {
            term: "Once (default)",
            description:
              "Charged one time and added on top of what the investor wires, so the full commitment goes into the asset. Example: a $100,000 commitment with a 2% one-time fee means the investor wires $102,000, and $100,000 is invested.",
          },
          {
            term: "Annual",
            description:
              'For funds: "annually in advance at each closing". It is not added to the wire amount, so it is paid out of the investor\'s capital. A fund that charges 2% a year over several years invests noticeably less than it raised, which is why the frequency matters as much as the rate.',
          },
          {
            term: "Notes",
            description:
              "Free text printed in the legal documents, for anything the percentage and frequency can't express.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "At close, the deal's management fee is the sum of each investor's own fee (investors on an access link can have a different rate), and it is deducted before the money is wired to the asset. Fees are taken at close and cannot be paid later. Required (with carry) to submit the deal.",
      },
    ],
  },
  {
    id: "setup-carry",
    group: "Setup",
    title: "Carry",
    blocks: [
      {
        type: "paragraph",
        text: "Carried interest is the fund manager's share of the profits, as a percentage. It is how the manager gets paid for picking a good deal: no profit, no carry. That aligns the manager with investors, since they only earn more when investors do. It is usually 20%, sometimes lower to attract investors or higher for a sought-after deal.",
      },
      {
        type: "heading",
        text: "How it works on an exit",
      },
      {
        type: "flow",
        steps: [
          "Investors put $1,000,000 into the SPV.",
          "The company is acquired and the SPV's shares are now worth $3,000,000.",
          "Investors first get their $1,000,000 back. The profit is $2,000,000.",
          "With 20% carry, the manager earns $400,000 of that profit.",
          "Investors receive $2,600,000 in total: a 2.6x on their money instead of 3x.",
        ],
      },
      {
        type: "list",
        items: [
          "Carry is charged at distribution, and only on the new profit of that distribution: a partial exit doesn't charge carry again on profit already taxed by an earlier one.",
          "Each investor's own carry is used first, the deal carry is the fallback. This is how access links give some investors a lower rate.",
          "0 is valid. Required (with the management fee) to submit the deal.",
          "Custom carry calculation and carry notes can be set in the V4 legal documents.",
        ],
      },
      {
        type: "paragraph",
        text: "Fee recipients can split the carry and the management fee between several people, for example a co-lead or a scout who brought the deal (see Closing · Fee recipients).",
      },
    ],
  },
  {
    id: "setup-access-links",
    group: "Setup",
    title: "Access links (custom fees)",
    blocks: [
      {
        type: "paragraph",
        text: "Shareable links that let some investors invest on different terms than the deal defaults. Found in Setup › Fees. They answer a common commercial need: a manager wants to give better terms to a large investor, an early backer, or a community they partner with, without changing the deal for everyone.",
      },
      {
        type: "paragraph",
        text: "Example: a deal at 2% management fee and 20% carry, with one link at 0% and 10% for an anchor investor bringing $500,000. Everyone invests in the same vehicle and the same asset; only what that investor pays the manager changes.",
      },
      {
        type: "list",
        items: [
          "Each link has its own management fee and carry; a new link copies the deal terms.",
          "Optional expiry and max uses (empty = unlimited), so a discount can be limited in time or to a set number of investors. Can be switched on or off.",
          "Status: active, off, expired or full. Visits and uses (live investments) are counted.",
          "The terms of the link are copied onto the investment on the server; the investor never sets them.",
          "The investor's SPV agreement is rendered with the link's terms, so what they sign matches what they pay.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        title: "Not a side letter",
        text: "The code allows link terms below the deal terms, so a fund manager can offer a discount. A side letter is a different concept (a separate agreement with extra rights for one investor): don't use links to model it.",
      },
    ],
  },
  {
    id: "setup-assets",
    group: "Setup",
    title: "Assets",
    blocks: [
      {
        type: "paragraph",
        text: "What the SPV or fund buys with investors' money. It is what investors are really exposed to: the deal's value goes up and down with its assets. At least one asset is required to submit.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Stock",
            description:
              "Newly issued shares in a startup's funding round (primary): the money goes to the company.",
          },
          {
            term: "Secondary",
            description:
              "Existing shares bought from a current shareholder (a founder, an employee, an early investor). The money goes to the seller, not the company, often at a different price than the last round.",
          },
          {
            term: "SPV into fund / SPV into SPV",
            description:
              "The deal invests into another vehicle rather than directly into a company. Investors then bear two layers of fees and carry: ours and the underlying vehicle's.",
          },
          {
            term: "Crypto, real estate, other",
            description:
              "Assets that need specific custody, banking or valuation work, which is why they are Premium SPVs.",
          },
        ],
      },
      {
        type: "list",
        items: [
          "A Fund holds several assets through one entity, spreading the risk; an SPV usually holds one, so investors' outcome rides on a single company.",
          "Each asset can be linked to a company and its funding round, which records the price paid and the valuation it was bought at.",
          "Asset valuations drive the deal's estimated multiple: today's value of the assets divided by what was paid for them (see Post-close · Valuations).",
        ],
      },
    ],
  },
  {
    id: "setup-entity",
    group: "Setup",
    title: "Entity",
    blocks: [
      {
        type: "paragraph",
        text: "The legal entity formed to invest in the asset. Pick an existing entity or create one. Required to submit, and required before a close can be requested.",
      },
      {
        type: "paragraph",
        text: "The entity is what makes an SPV work financially. Investors buy units in the entity, and the entity is the one shareholder of the company. The company gets one line on its cap table instead of dozens of small investors, and each investor's exposure is limited to what they put in.",
      },
      {
        type: "list",
        items: [
          "The entity is usually taxed as a pass-through: it pays no tax itself, and its gains and losses flow to investors, who receive a K-1 each year (see the Taxes page).",
          "The entity holds the ledger, the bank account and the unit classes, so every dollar in and out of the deal is recorded against it.",
          "It needs its own tax ID (EIN) to open a bank account and file taxes. The EIN is obtained with an SS-4 (filled in from the V4 legal documents); the EIN letter is stored in the deal files.",
          "Archiving an entity archives its draft deals, and fails if one of its deals has left draft.",
        ],
      },
      {
        type: "paragraph",
        text: "Formation and entity types are covered on the Entities page.",
      },
    ],
  },
  {
    id: "setup-fund-manager-advisor",
    group: "Setup",
    title: "Fund Manager & Advisor",
    blocks: [
      {
        type: "paragraph",
        text: "Assign the identities that hold the fund manager and advisor seats. These are the people legally responsible for the deal, so they are also the ones investors are trusting with their money.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Fund manager",
            description:
              "Brings the deal, sets the terms and decides for the vehicle. They earn the management fee and the carry, and they owe investors a duty to act in their interest. Their identity is required to submit.",
          },
          {
            term: "Advisor",
            description:
              'The investment adviser on record. Managing other people\'s money for a fee usually requires being a registered or exempt reporting adviser, so counsel decides whether one is needed. Optional: tick "Counsel stated no advisor required" when none is needed; assigning an advisor later clears it.',
          },
        ],
      },
      {
        type: "list",
        items: [
          "Before submission, both seats are edited freely in Setup.",
          "Once the deal leaves draft, roles are locked, because investors committed on the basis of who runs the deal: the seats can only change through a role change request (see Special cases · Fund manager change).",
        ],
      },
    ],
  },
  {
    id: "setup-banking",
    group: "Setup",
    title: "Banking Setup",
    blocks: [
      {
        type: "paragraph",
        text: "How the deal's bank account will be opened. Required to submit. Each deal gets its own account in the entity's name: investors wire into it, fees are taken from it at close, and the rest is wired out to the asset. Keeping one account per deal means investors' money is never mixed with another deal's or with ours.",
      },
      {
        type: "table",
        columns: ["Provider", "When", "Extra cost"],
        rows: [
          [
            "Standard Allocations Banking Provider",
            "Most deals: US dollars in, US dollars out",
            "None",
          ],
          [
            "Crypto Allocations Banking Provider",
            "The deal buys or holds digital assets",
            "$2,000 add-on + 1% transaction fee (e.g. $10,000 on $1M)",
          ],
          [
            "Custom Banking Provider",
            "The manager needs to bank elsewhere",
            "$2,000 one-time add-on",
          ],
          [
            "Custom Crypto Provider",
            "Digital assets with the manager's own custodian",
            "$2,000 one-time add-on",
          ],
        ],
      },
      {
        type: "paragraph",
        text: "Compliance questions (all must be answered, and every yes needs a description). Banks must know where money comes from and where it goes, so these answers decide whether the account can be opened and how quickly:",
      },
      {
        type: "list",
        items: [
          "Investors residing outside the U.S.? Foreign investors mean incoming international wires and extra checks. If yes: which countries.",
          "Any politically exposed persons (PEPs) involved? If yes: each PEP, their role and their relationship to the deal.",
          "Investors investing through trusts, funds or entities? If yes: the entity types and their jurisdictions.",
          "International transactions in or out of the account? If yes: the countries, purpose and approximate volume.",
          "Funds sent by third parties, not investors' own accounts? If yes: who sends them, and why.",
          "Funds in a currency other than USD? If yes: which currencies, and roughly what share.",
          "Company builds blockchain or digital-asset products? If yes: the products or services.",
          "Company performs regulated activities (VASP, MSB, broker-dealer)? If yes: the activities, the jurisdictions and any licenses or registrations.",
          "Company in cannabis, gambling, firearms or adult industries? If yes: the business activities and licenses.",
          "Exposure to OFAC-sanctioned regions? If yes: the regions and the nature of the exposure.",
        ],
      },
      {
        type: "paragraph",
        text: "Banking address. The partner bank runs KYC on the physical address it associates with the account, so the fund manager gives one in Banking Setup: street, city, state, ZIP, whether it is an office, coworking space or residence, and whether it is owned, leased or sublet. It can be copied from the fund manager's own identity when that address is a complete U.S. one; the copy is stored on the deal (`deal_details.banking_address_*`) and does not follow later edits to the identity. The fund manager must tick that it is a real physical address before saving.",
      },
      {
        type: "callout",
        tone: "warning",
        title: "A real U.S. address, or the account is delayed",
        text: "Registered agent addresses, PO boxes and virtual mailboxes are rejected by the bank. A fund manager without a U.S. address can give a non-U.S. one (`banking_address_is_non_us`), but it triggers the bank's additional review and can delay opening the account. Check the address in Banking Operating Procedure before filling in the bank application.",
      },
      {
        type: "paragraph",
        text: "Accounts, wires and reconciliation are covered on the Banking page.",
      },
    ],
  },
  {
    id: "setup-legal-documents",
    group: "Setup",
    title: "Legal Documents",
    blocks: [
      {
        type: "paragraph",
        text: "The documents investors sign. They are what turns a pledge into a binding investment, and they fix the economics in writing: how much each investor puts in, the fees and carry, how and when profits are paid out, and what the manager can and can't do. If the platform and the documents ever disagree, the documents win.",
      },
      {
        type: "paragraph",
        text: "The deal's documents provider decides how they are made:",
      },
      {
        type: "list",
        items: [
          "V4 (platform, the default): the SPV agreement is generated in the platform from the deal terms (fund manager, administrative manager, fees, minimum subscription…), previewed, then signed by the fund manager.",
          "V3 (DocSpring template, signing in DocuSeal): an older template-based flow.",
        ],
      },
      {
        type: "paragraph",
        text: "An unsigned SPV agreement only shows a warning on submit, but it must be signed before investors onboard (unless custom docs are provided). Each investor's agreement uses their own fee terms. See the Legal Documents page for templates.",
      },
    ],
  },
  {
    id: "setup-data-room",
    group: "Setup",
    title: "Data Room",
    blocks: [
      {
        type: "paragraph",
        text: "Confidential documents investors use to do their due diligence before committing: typically the pitch deck, the term sheet, financial statements, the cap table and any investor update. The better the data room, the easier it is for investors to say yes, and the stronger the record that they were properly informed.",
      },
      {
        type: "paragraph",
        text: "Companies often share these documents under confidentiality, so the room protects them with optional passcodes:",
      },
      {
        type: "list",
        items: [
          "Files uploaded here are deal files of type data-room.",
          "Passcodes: 6 digits, any number per deal, each with a label (one per investor group, for example). Can be disabled, regenerated, deleted, or locked to its first user.",
          "No passcode: open to signed-in investors. All passcodes disabled: closed.",
          "Fund manager, team and account members skip the gate.",
          "Investors accept an NDA notice on entry; files show a watermark.",
          "Analytics: room entered, file opened, file downloaded, possible screenshot, access denied — with passcode, IP and country. It shows the manager which investors are seriously looking at the deal.",
        ],
      },
    ],
  },
  {
    id: "setup-submit-deal",
    group: "Setup",
    title: "Submit deal",
    blocks: [
      {
        type: "paragraph",
        text: "Submitting hands the deal to Allocations to make it real: legal documents, bank account, then opening it to investors. Before that point the manager can change anything for free; after it, changes to the deal info can cost money because documents and accounts may need to be redone.",
      },
      {
        type: "paragraph",
        text: "The Submit button appears only on a draft deal whose Setup checklist is 100% complete:",
      },
      {
        type: "list",
        items: [
          "Product type",
          "Management fee and carry",
          "Fund manager identity",
          "Account",
          "Entity",
          "At least one asset",
          "Banking Setup fully answered",
        ],
      },
      {
        type: "paragraph",
        text: 'The fund manager accepts the terms (self-service model, accurate setup form, costs if deal info changes, MSA). The deal moves to submitted, a Slack alert and a "deal submitted" email go out, and draft reminders stop. Ops then reviews it, creates the legal documents and the bank account, and moves it to onboarding.',
      },
    ],
  },
  // ---- Fundraise ----
  {
    id: "fundraise-inviting-investors",
    group: "Fundraise",
    title: "Inviting investors",
    blocks: [
      {
        type: "paragraph",
        text: "The fundraise is when the manager turns their network into money in the bank. Invites are how it starts: the fund manager invites investors by email from Hercules, and each invite tells the investor how much the manager hopes they will put in and by when. Clear amounts and deadlines are what get a round funded on time.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Amount",
            description:
              "The suggested ticket, pre-filled on the invest page. It defaults to the deal's minimum investment. Setting it to what the manager discussed with that investor makes saying yes a single click.",
          },
          {
            term: "Signing deadline",
            description:
              "When the investor should have signed their agreement. Defaults to the closing date minus 2 business days.",
          },
          {
            term: "Wiring deadline",
            description:
              "When the money should be in the deal's bank account. Also defaults to the closing date minus 2 business days, which leaves the team time to match wires to investors before the money goes out to the asset.",
          },
          {
            term: "Unit class",
            description:
              "On deals with units, the invite picks an active unit class (the default class otherwise). A unitized deal with no active class can't send invites.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "Example: a deal closing on Friday, June 20 with a $5,000 minimum. An invite sent with no changes asks for $5,000, with signing and wiring deadlines of Wednesday, June 18.",
      },
      {
        type: "list",
        items: [
          "A new email gets a user account created. Disposable addresses are rejected; blocked domains are silently dropped.",
          "The email links to the invest page with the amount pre-filled.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        title: "An invite is not money",
        text: "An invitation is not an investment: the investment is created when the investor signs. Invites sent say nothing about how much will actually be raised.",
      },
    ],
  },
  {
    id: "fundraise-invest-page",
    group: "Fundraise",
    title: "Invest page",
    blocks: [
      {
        type: "paragraph",
        text: "What the investor sees and does. It is where an interested investor becomes a legal commitment: they choose an amount, sign the agreement, then wire the money. The page makes sure those steps happen in that order, because money that arrives without a signed agreement is money the vehicle has no right to hold.",
      },
      {
        type: "list",
        items: [
          "Investing is open only when the deal is onboarding, closing, close-requested (or test).",
          "Signing is enabled once there is an agreement on file, an amount, a complete identity and the terms box is ticked.",
          "The investor can name someone else to sign, for example a family office or a company director signing for an entity: the investment is then created as invited instead of signed.",
          'When pledges are enabled, a "Submit Non-Binding Pledge" button is shown.',
        ],
      },
      {
        type: "heading",
        text: "Wire instructions",
      },
      {
        type: "paragraph",
        text: "Wire instructions are shown only after the investor ticks a warning to sign before wiring. The payment type changes what the investor actually pays:",
      },
      {
        type: "table",
        columns: ["Type", "Typical investor", "Extra cost"],
        rows: [
          ["Domestic ACH/wire", "US investor with a US bank account", "No crypto fee"],
          [
            "International wire",
            "Investor banking outside the US",
            "No crypto fee; their bank may charge",
          ],
          ["USDC / USDT", "Investor paying in stablecoins", "1% fee in and 1% out"],
        ],
      },
      {
        type: "paragraph",
        text: "Example: an investor puts $50,000 in USDC. The 1% fee in is $500 on the way in, and another 1% applies when money goes out in crypto.",
      },
    ],
  },
  {
    id: "fundraise-investor-onboarding",
    group: "Fundraise",
    title: "Investor onboarding (KYC and accreditation)",
    blocks: [
      {
        type: "paragraph",
        text: 'Before a vehicle can take someone\'s money, the law requires knowing who they are (KYC, "know your customer", to stop money laundering) and that they are allowed to invest in private deals (accreditation, because these deals carry more risk and less disclosure than public stocks). An investor who fails these checks cannot be part of the close, so onboarding is what turns signatures into money that can actually be used.',
      },
      {
        type: "paragraph",
        text: "Every investment is made through an identity: individual or entity (including trusts). The same person can invest personally and through their company; each is its own identity.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Basic details",
            description:
              "Legal name, date of birth, tax ID, phone, citizenship, address, email. The tax ID is also what the investor's K-1 is issued against each year.",
          },
          {
            term: "Accreditation",
            description:
              "At least one is required. It shows the investor meets the SEC's wealth or income tests for private deals (for individuals, generally $200,000 of income or $1M of net worth excluding their home).",
          },
          {
            term: "Beneficial owner and signer (entities)",
            description:
              "Entities need a beneficial owner (or a control person for trusts) and a signer: the real people behind the money, and the person allowed to commit it.",
          },
          {
            term: "ID document (non-US individuals)",
            description: "Non-US individuals must provide an ID document.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "KYC runs through Onfido and is valid for 6 months. It does not block signing, but the investment isn't finalized until KYC is current. Details on the Identities page.",
      },
      {
        type: "callout",
        tone: "warning",
        title: "No accepted KYC, no close",
        text: "KYC must be accepted for every investor before a close. One investor with expired or missing KYC holds up the close for everyone, so chase it while the raise is still open, not on closing day.",
      },
    ],
  },
  {
    id: "fundraise-investment-statuses",
    group: "Fundraise",
    title: "Investment statuses",
    blocks: [
      {
        type: "paragraph",
        text: "Each investment moves from a promise to cash the deal can use. The status says where it stands, and it decides whether that investor's money can go into a close. A manager reading the raise should care less about how many investors signed and more about how many are completed.",
      },
      {
        type: "flow",
        steps: ["invited", "signed", "committed", "wired", "completed"],
      },
      {
        type: "terms",
        items: [
          {
            term: "invited",
            description: "Someone else still has to sign.",
          },
          {
            term: "signed",
            description:
              "The agreement is signed (the default when the investor signs themselves). The investor is legally committed, but no money has arrived.",
          },
          {
            term: "committed / wired",
            description: "Money expected / received.",
          },
          {
            term: "completed",
            description:
              "Fully done; only completed investments can be put on a close, so only their money reaches the asset.",
          },
          {
            term: "`partially_transferred_out` / `transferred_out`",
            description:
              "Set by membership transfers, when an investor sells or gives part or all of their stake to someone else.",
          },
          {
            term: "archived",
            description: "Removed; also drops the investment from its closes.",
          },
        ],
      },
      {
        type: "heading",
        text: "Signed amount vs cash received",
      },
      {
        type: "paragraph",
        text: "`subscription_amount` is what the investor signed for. `capital_wired_amount` is the cash received. The two don't always match: an investor who signed for $25,000 may wire $24,970 because their bank took a $30 fee. The gap is what ops must resolve before the investment can be treated as fully funded. Ops set statuses and wired amounts in Poseidon.",
      },
    ],
  },
  {
    id: "fundraise-pledges",
    group: "Fundraise",
    title: "Pledges",
    blocks: [
      {
        type: "paragraph",
        text: "A pledge is a non-binding interest: a minimum and maximum amount plus contact details. It is not an investment. Pledges help a manager size demand early (often before the documents are ready) and decide how big an allocation to ask the company for, without anyone being on the hook yet.",
      },
      {
        type: "table",
        columns: ["", "Pledge", "Commitment"],
        rows: [
          [
            "Binding",
            "No: the investor can walk away",
            "Yes: an investment with a signed agreement",
          ],
          ["Amount", "A minimum and maximum range", "One subscription amount"],
          ["Counts in the raise", "No", "Yes, in the signed total"],
        ],
      },
      {
        type: "paragraph",
        text: "Example: 30 pledges ranging from $10,000 to $50,000 each add up to between $300,000 and $1,500,000 of interest. That range tells the manager whether to ask the company for a $500,000 or a $1,000,000 allocation, but none of it is money until each investor signs.",
      },
      {
        type: "list",
        items: [
          "Turned on per deal (`pledges_enabled`).",
          "Tracked on a Kanban: draft, discussion, invited, signed, wired.",
          "Refused on archived or cancelled deals.",
        ],
      },
    ],
  },
  {
    id: "fundraise-amount-requests",
    group: "Fundraise",
    title: "Amount requests",
    blocks: [
      {
        type: "paragraph",
        text: "An investor asks to change their subscription amount after signing. Their signed amount is a legal commitment, and the manager has to fit everyone inside the allocation the company granted. So the change is a request the manager approves, not something the investor edits on their own.",
      },
      {
        type: "paragraph",
        text: "Example: an investor signed for $50,000 and asks to go up to $75,000. If the round is already full, the manager can reject it; if there is room, approval writes $75,000 and a new agreement is generated for that amount.",
      },
      {
        type: "list",
        items: [
          "The fund manager approves or rejects it in Hercules; Poseidon lists the requests.",
          "Approval writes the new amount, rebuilds the agreement and moves a completed investment back to signed, since the money received no longer matches what was signed. Rejection changes nothing.",
          "One pending request at a time. Not allowed on archived or transferred-out investments.",
          "Platform deals must be in onboarding. Migration and external investments update directly without approval.",
        ],
      },
    ],
  },
  {
    id: "fundraise-wires-and-progress",
    group: "Fundraise",
    title: "Wires and raise progress",
    blocks: [
      {
        type: "paragraph",
        text: "Money only counts once we know whose it is. Investors wire into the deal's bank account, and each wire must be matched to the right investment so that every investor gets credit for exactly what they sent: their ownership, fees and future distributions are all computed from it.",
      },
      {
        type: "heading",
        text: "Matching wires",
      },
      {
        type: "list",
        items: [
          "Ops link each incoming bank transaction to an investment by hand in Poseidon (Link investment).",
          "Suggestions rank investments by investor name or email in the transaction text and by how close the amount is.",
          "Only transactions on the deal's own bank account can be linked, so one deal's money can never be credited to another.",
        ],
      },
      {
        type: "callout",
        tone: "warning",
        title: "Check the name, not just the amount",
        text: "Two investors often wire the same round number. Linking a wire to the wrong investment credits the wrong person's ownership and distributions. Confirm the sender's name or email in the transaction text before linking, especially when several suggestions have the same amount.",
      },
      {
        type: "heading",
        text: "Raise progress",
      },
      {
        type: "terms",
        items: [
          {
            term: "Signed total",
            description:
              "Sum of subscription amounts: what investors have promised. Archived investments are left out.",
          },
          {
            term: "Wired total",
            description:
              "Sum of capital wired: what is actually in the bank. Archived investments are left out.",
          },
          {
            term: "SPV vs Fund labels",
            description: "SPVs show Wired / Signed; Funds show Contributed / Committed.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "Example: a $1,000,000 target with $1,300,000 signed shows a full bar labelled 130%. The bar caps at 100%, the label shows the real figure. A round above 100% means the manager has more demand than allocation, and the difference has to be cut back or refunded.",
      },
    ],
  },
  // ---- Closing ----
  {
    id: "closing-what-is-a-close",
    group: "Closing",
    title: "What a close is",
    blocks: [
      {
        type: "paragraph",
        text: "Up to the close, investors' money sits in the SPV's bank account and can still be refunded. The close is the moment it leaves the SPV to buy the asset: fees are taken, the rest is wired to the company, and investors now own a share of the investment instead of cash. In Poseidon a close is its own record, not a deal status.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Requested date and amount",
            description:
              "What the fund manager asked for: when they want the money to go out and how much of the raise this close covers.",
          },
          {
            term: "Closed date",
            description:
              "When the close actually happened. It is the date investors' money was put to work, and the date the Form D deadline runs from.",
          },
          {
            term: "Portfolio wire amount",
            description:
              "What was actually sent to the company, after fees. It is usually less than the gross amount investors wired.",
          },
          {
            term: "Status, assignee, notes",
            description:
              "Where the close is in ops' review, who on our side owns it, and free-text context.",
          },
        ],
      },
      {
        type: "list",
        items: [
          "A close is linked to the investors it includes and to the asset transactions.",
          "Creating the first close moves the deal to closing automatically.",
          "A deal can have several closes (Premium SPV and Fund only). Each one releases the money raised since the previous close.",
        ],
      },
      {
        type: "callout",
        tone: "warning",
        title: "Reading the close date",
        text: 'Never answer "when did this deal close" from the deal status or updated date: read its closes. The deal status can be changed long after the money moved, and a deal with several closes has several dates.',
      },
    ],
  },
  {
    id: "closing-request-close",
    group: "Closing",
    title: "Request a close",
    blocks: [
      {
        type: "paragraph",
        text: "The fund manager asks for the money to be released when the company is ready to receive it, usually on the deadline of its own round. Everything checked here protects investors: their money only leaves the SPV once we know who they are, where it is going and on what signed terms.",
      },
      {
        type: "paragraph",
        text: 'The fund manager clicks "Request a Close" in Hercules with a date, an amount and optional notes and investors.',
      },
      {
        type: "heading",
        text: "Requirements",
      },
      {
        type: "terms",
        items: [
          {
            term: "Deal state",
            description:
              "The deal has an entity and isn't closed, archived, cancelled or terminated. Without an entity there is no legal vehicle to hold the shares.",
          },
          {
            term: "Investments",
            description:
              "Only completed investments not already on another close can be added, so the same dollars are never sent twice.",
          },
          {
            term: "Wire recipients",
            description:
              "One for the portfolio company, plus one for the fund manager if a management fee applies: the fee has to be paid somewhere.",
          },
          {
            term: "Investment agreement",
            description:
              "A signed investment agreement on the asset: the contract that says what the SPV gets for its money.",
          },
          {
            term: "KYC",
            description:
              "KYC accepted for every investor. Sending money from an investor we haven't verified is an anti-money-laundering risk.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "Documents sent to support: asset purchase agreement, portfolio company wire instructions, manager wire instructions if fees are due. A close takes up to 2 business days, so a manager facing a company deadline should request it at least 2 business days ahead.",
      },
    ],
  },
  {
    id: "closing-statuses",
    group: "Closing",
    title: "Close statuses",
    blocks: [
      {
        type: "paragraph",
        text: "The status tells everyone whether the money is still in the SPV or already at the company. Investors, the manager and finance all rely on it, so it should always reflect what really happened to the wire.",
      },
      {
        type: "paragraph",
        text: "Set by ops in Poseidon: `submitted`, `processing`, `audit`, `approved`, `completed`, `cancelled`, `archived`, `test`.",
      },
      {
        type: "list",
        items: [
          "The closed date only counts as real once the close is `completed`; before that it is planned.",
          "A status change notifies admins and the deal owner.",
          "Moving the deal itself to closed is a separate, manual step.",
        ],
      },
    ],
  },
  {
    id: "closing-tracker",
    group: "Closing",
    title: "Closing tracker",
    blocks: [
      {
        type: "paragraph",
        text: "The fee calculation for one close, reviewed by the fund manager before money moves. It answers the question every manager asks at close: of the money investors sent, how much reaches the company and where does the rest go? Once wired, money is very hard to get back, so this is the last chance to catch a wrong number.",
      },
      {
        type: "paragraph",
        text: "Math: gross capital received − portfolio company investment − Allocations fees − blue sky fees − management fee = net SPV balance.",
      },
      {
        type: "heading",
        text: "Worked example (illustrative numbers)",
      },
      {
        type: "flow",
        steps: [
          "Investors wire $1,020,000: $1,000,000 of commitments plus a 2% one-time management fee.",
          "Management fee: $20,000 goes to the fund manager.",
          "Allocations and blue sky fees: say $12,000 together.",
          "Portfolio company investment: $988,000 is wired to the company.",
          "Net SPV balance: $0. Anything left over stays in the SPV and must be explained.",
        ],
      },
      {
        type: "paragraph",
        text: "Flow: draft → `pending_review` → `approved` or `changes_requested`.",
      },
      {
        type: "list",
        items: [
          "Ops send the tracker to the fund manager while it is `pending_review`.",
          "The fund manager approves or requests changes in Hercules.",
        ],
      },
      {
        type: "paragraph",
        text: "Fees deducted from the portfolio wire: regulatory, blue sky, SPV, banking and management fees.",
      },
    ],
  },
  {
    id: "closing-wire-recipients",
    group: "Closing",
    title: "Wire recipients and wire instructions",
    blocks: [
      {
        type: "paragraph",
        text: "These decide where investors' money ends up. A wrong account number or a fake set of instructions sent by a fraudster means the money is gone, so they are filed once and checked before every use.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Wire recipient",
            description:
              "Where money is sent (portfolio company, fund manager). Filed under the fund manager's identity and linkable to several deals. Methods: wire, ACH, Swift, crypto, broker.",
          },
          {
            term: "Wire instructions",
            description:
              "Uploaded files (bank or crypto), read automatically then checked in a verify step before use.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "The portfolio company wire recipient is required before a close.",
      },
      {
        type: "callout",
        tone: "warning",
        title: "Changed instructions",
        text: "Wire fraud often arrives as an email saying the company's bank details have changed. Never use new instructions that skipped the verify step.",
      },
    ],
  },
  {
    id: "closing-signature-block",
    group: "Closing",
    title: "Signature block",
    blocks: [
      {
        type: "paragraph",
        text: "When the SPV buys shares, the SPV itself is the buyer and signs the company's closing documents, not each investor. That is what lets dozens of investors appear as a single line on the company's cap table.",
      },
      {
        type: "paragraph",
        text: "The signature block is the SPV's own (entity name, fund manager's name, email and address) that ops share with the target company for its closing documents. It is not the investors' signature.",
      },
    ],
  },
  {
    id: "closing-invoices",
    group: "Closing",
    title: "Invoices",
    blocks: [
      {
        type: "paragraph",
        text: "How Allocations gets paid for setting up and running the vehicle. Invoices make our fees visible as a separate line, so the manager and investors can see exactly what the SPV paid us.",
      },
      {
        type: "paragraph",
        text: "Allocations bills its fees through invoice requests, pre-filled from the close's fee schedule (product fee, Allocations, blue sky and other fees).",
      },
      {
        type: "terms",
        items: [
          {
            term: "Status",
            description: "Invoice request: `pending` → `processed` → `confirmed` → `paid`.",
          },
          {
            term: "Payer",
            description:
              "Paid by the fund manager, or pulled from the deal's bank account (in which case investors' capital bears the cost).",
          },
          {
            term: "Recurrence",
            description: "One-time, monthly or annual.",
          },
        ],
      },
      {
        type: "list",
        items: [
          "Invoices are stored Xero-style and linked to the deal in Manage › Invoices.",
          "Distributions also generate fee lines. Month-end is covered on the Finance page.",
        ],
      },
    ],
  },
  {
    id: "closing-fee-recipients",
    group: "Closing",
    title: "Fee recipients",
    blocks: [
      {
        type: "paragraph",
        text: "Who receives the management fee and the carry, when it is split. Managers often share their economics with a co-lead or a scout who brought the deal. It changes who gets paid, not what investors pay.",
      },
      {
        type: "list",
        items: [
          "Each recipient has a name, email, management fee share and carry share (percent of the deal fee).",
          "Shares can't add up to more than 100%.",
          "Effective rate shown = deal rate × share.",
        ],
      },
      {
        type: "paragraph",
        text: "Example: on a deal with 20% carry, a scout with a 25% carry share has an effective rate of 20% × 25% = 5%. On a $2,000,000 profit, the carry is $400,000: the scout gets $100,000 and the rest goes to the other recipients.",
      },
    ],
  },
  {
    id: "closing-regulatory-filings",
    group: "Closing",
    title: "Regulatory filings (Form D, blue sky)",
    blocks: [
      {
        type: "paragraph",
        text: "Selling private securities without registering them is only allowed if the offering is reported. Form D tells the SEC the offering exists; blue sky filings tell each state where investors live. Every close triggers SEC and state filings, tracked per deal. The state fees are a real cost of the raise, paid out of the close.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Form D",
            description: "Due within 15 days of the closed date, flagged overdue after.",
          },
          {
            term: "Blue sky",
            description:
              "State filings. A flat $160 service fee is charged once per deal, on the first close with state fees; later closes only pay the difference.",
          },
          {
            term: "Fields",
            description: "CIK, CCC, PMAC, file number, blue sky date and total.",
          },
          {
            term: "Status",
            description:
              "`draft`, `filed`, `amended`. Queue stages: Pending CIK, Pending Form D, Pending Blue Sky fee.",
          },
        ],
      },
      {
        type: "callout",
        tone: "warning",
        title: "Late Form D",
        text: "A late Form D is a compliance breach for the vehicle and the manager, and late state filings can add penalties. Watch the overdue flag after every close, not only the first.",
      },
      {
        type: "paragraph",
        text: "Policy is on the Compliance page.",
      },
    ],
  },
  // ---- Post-close ----
  {
    id: "post-close-ledger",
    group: "Post-close",
    title: "Ledger and capital accounts",
    blocks: [
      {
        type: "paragraph",
        text: "Once the money is wired to the asset, the deal becomes something to keep score of. The ledger is that score: it records every dollar that came into the entity, every fee paid and every dollar sent out. The capital account is each investor's own slice of it. It answers the question every investor eventually asks: how much did I put in, how much is it worth today, and how much have I already received back?",
      },
      {
        type: "paragraph",
        text: "Each entity has one double-entry ledger, created with base accounts: cash, asset purchased, payables, expenses, admin expense, management fees, Allocations fees, third-party fees. Double-entry means every movement is recorded twice (where the money came from and where it went), so the books always balance and a missing dollar shows up. Each investor has a capital account.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Committed",
            description:
              "What the investor promised to invest. It is the starting point of their stake.",
          },
          {
            term: "Partnership %",
            description:
              "The investor's share of the entity. It decides their share of every gain, loss and distribution.",
          },
          {
            term: "Opening balance",
            description: "What the capital account was worth at the start of the period.",
          },
          {
            term: "Contributions",
            description: "Money the investor actually put in during the period.",
          },
          {
            term: "Income and gains",
            description:
              "The investor's share of what the entity earned or of the rise in value of its assets. Losses reduce the account the same way.",
          },
          {
            term: "Distributions",
            description: "Money paid back to the investor during the period.",
          },
          {
            term: "Closing balance",
            description:
              "Opening balance + contributions + income and gains − distributions: what the investor's stake is worth at the end of the period.",
          },
        ],
      },
      {
        type: "heading",
        text: "A capital account over one year",
      },
      {
        type: "flow",
        steps: [
          "An investor commits $100,000 to an SPV that raises $1,000,000: a 10% partnership share.",
          "They wire $100,000. Contributions: $100,000.",
          "The company raises a new round at a higher price and the SPV's shares are marked up by $500,000. The investor's 10% share of the gain: $50,000.",
          "No distribution yet.",
          "Closing balance: $0 + $100,000 + $50,000 − $0 = $150,000.",
        ],
      },
      {
        type: "paragraph",
        text: "Financials tabs in Poseidon: NAV, balance sheet, income statement, changes in capital, cash flows, schedule of investments, IRR, capital accounts, investor statements. NAV (net asset value) is what the entity's assets are worth minus what it owes; IRR is the yearly return rate, which takes into account how long the money was invested.",
      },
      {
        type: "list",
        items: [
          "Capital account: committed, partnership %, opening balance, contributions, income, gains, distributions, closing balance.",
          "Schedule of investments: per asset, cost, valuation, multiple, gross IRR. Gross means before fees and carry, so investors' own return is lower.",
        ],
      },
    ],
  },
  {
    id: "post-close-units",
    group: "Post-close",
    title: "Units",
    blocks: [
      {
        type: "paragraph",
        text: "Units express each investor's ownership in the entity. They work like shares of the vehicle: an investor who holds 10% of the units is entitled to 10% of what the entity owns and of what it pays out. Using units instead of dollar amounts keeps ownership fair when investors come in at different times or prices, because the number of units stays fixed while their value moves with the asset.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Unit class",
            description:
              "A category of units. Unit classes belong to the entity and have a conversion rate (capital per unit). Status: draft, active, closed. Draft classes are left out of totals.",
          },
          {
            term: "Conversion rate",
            description:
              "How many dollars of capital buy one unit. Units = contributed capital ÷ conversion rate (wired amount, else subscription amount).",
          },
          {
            term: "Manual override",
            description:
              "A unit count set by hand for one investor. Manual overrides survive rate changes.",
          },
        ],
      },
      {
        type: "heading",
        text: "Example",
      },
      {
        type: "flow",
        steps: [
          "The entity's active unit class has a conversion rate of $10 per unit.",
          "Investor A wired $100,000: 10,000 units. Investor B has subscribed $50,000 but not wired yet: 5,000 units, based on the subscription amount until the wire arrives.",
          "The entity has 15,000 units in total, so A owns two thirds and B one third.",
          "If the entity later distributes $30,000, A receives $20,000 and B $10,000 (before carry).",
        ],
      },
      {
        type: "list",
        items: [
          "The Units tab shows when units are on for the whole entity (needs an active class) or for this deal only.",
        ],
      },
      {
        type: "callout",
        tone: "warning",
        title: "Check overrides after a rate change",
        text: "Manual overrides are not recalculated when the conversion rate changes. An override that was right under the old rate can leave one investor with too many or too few units, and so with the wrong share of every future distribution.",
      },
    ],
  },
  {
    id: "post-close-valuations",
    group: "Post-close",
    title: "Valuations and estimated multiple",
    blocks: [
      {
        type: "paragraph",
        text: "A private company's shares don't have a daily market price, so their value only changes when something happens: a new funding round, a secondary sale, a write-down. Each valuation is that new mark. The estimated multiple turns it into the number investors care about: how many times their money the investment is worth today, on paper. Nothing is paid out until a distribution, so it is an estimate, not cash.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Asset valuation",
            description:
              "Share price, shares, valuation, amount and date. The oldest one is the purchase basis: what the deal paid, which every later mark is compared against.",
          },
          {
            term: "Estimated multiple",
            description:
              "Computed on the deal from asset valuations and funding rounds; on multi-asset deals the highest wins. Default 1x, meaning the investment is worth what was paid.",
          },
          {
            term: "0x",
            description: "Written off: the asset is considered worthless. 0x is never shown as 1x.",
          },
        ],
      },
      {
        type: "heading",
        text: "A mark-up to a multiple",
      },
      {
        type: "flow",
        steps: [
          "The SPV buys 100,000 shares at $10 each: $1,000,000. This first valuation is the purchase basis.",
          "Two years later the company raises a new round at $25 per share.",
          "A new valuation is recorded: 100,000 shares × $25 = $2,500,000.",
          "Estimated multiple: $2,500,000 ÷ $1,000,000 = 2.5x.",
          "An investor who put in $100,000 holds a stake worth about $250,000 on paper, before carry.",
        ],
      },
      {
        type: "list",
        items: ["The fund manager sees the latest valuation per asset in Hercules."],
      },
      {
        type: "callout",
        tone: "warning",
        title: "Keep the purchase basis right",
        text: "The oldest valuation is used as the purchase basis. Recording a later mark with an earlier date, or editing the first valuation, changes the cost every multiple is measured against, so every investor sees the wrong performance.",
      },
      {
        type: "paragraph",
        text: "Methods are covered on the Valuations page.",
      },
    ],
  },
  {
    id: "post-close-investor-updates",
    group: "Post-close",
    title: "Investor updates",
    blocks: [
      {
        type: "paragraph",
        text: "Emails from the fund manager to the deal's investors. After the close, investors can't sell or check a market price, so updates are their main window on how their money is doing: a new round, a valuation change, an upcoming exit or a problem at the company. Regular, honest updates are also what makes investors come back for the manager's next deal.",
      },
      {
        type: "list",
        items: [
          "Only when the deal is onboarding, closing or closed.",
          "Only the fund manager or the deal owner can send.",
          "Recipients: investors filtered by investment status (not archived by default), minus exclusions, plus extra emails.",
          "Opens and clicks are tracked.",
        ],
      },
    ],
  },
  {
    id: "post-close-files-notes-timeline",
    group: "Post-close",
    title: "Files, notes, timeline and asset audit",
    blocks: [
      {
        type: "paragraph",
        text: "The deal's paper trail. Years after the close, these records are what proves who owns what, what was paid, and that the entity really holds the asset investors paid for. They are what auditors, tax preparers and investors ask for, and what settles a dispute.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Files",
            description:
              "Typed (subscription agreement, SPV agreement, EIN letter, data room, wire instructions, asset purchase agreement…). Poseidon can list deals missing a file type, for example every closed deal without its asset purchase agreement.",
          },
          {
            term: "Notes",
            description: "Internal notes on the deal.",
          },
          {
            term: "Timeline",
            description: "Deal created, entity created, investments, closes, distributions.",
          },
          {
            term: "Asset audit",
            description:
              'Asset overview, audit trail and documents, printed as a certificate "Verified & sealed by Allocations". It gives investors and auditors evidence that the asset exists and is held by the entity.',
          },
        ],
      },
    ],
  },
  {
    id: "post-close-distributions-taxes",
    group: "Post-close",
    title: "Distributions and taxes",
    blocks: [
      {
        type: "paragraph",
        text: "Distributions pay proceeds back to investors. This is where a paper gain becomes real money: when the company is acquired, goes public or the SPV sells its shares, the cash flows back to the entity and is shared out according to each investor's ownership, after the manager's carry.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Net to investor",
            description: "Gross − carry (on new profit only) − 1% if paid in crypto.",
          },
          {
            term: "Approval",
            description:
              "Draft → `pending_review` → approved or `changes_requested`. Money that leaves the entity can't easily come back, so every distribution is reviewed before it is paid.",
          },
        ],
      },
      {
        type: "heading",
        text: "A distribution in two steps",
      },
      {
        type: "flow",
        steps: [
          "Investors put $1,000,000 into the SPV, with 20% carry.",
          "A partial exit returns $1,500,000. Investors get their $1,000,000 back; the new profit is $500,000; carry is $100,000. Investors receive $1,400,000.",
          "A second exit returns another $1,500,000. All of it is new profit, so carry is $300,000 and investors receive $1,200,000.",
          "In total: $3,000,000 gross, $400,000 carry, $2,600,000 to investors. The first $500,000 of profit is never charged carry twice.",
        ],
      },
      {
        type: "paragraph",
        text: "Each investor's share is set by their ownership (units or partnership %) and their own carry rate (see Setup · Access links).",
      },
      {
        type: "heading",
        text: "Taxes",
      },
      {
        type: "paragraph",
        text: "The entity is a pass-through, so investors owe tax on their share of its gains each year, whether or not they received cash. The K-1 is the form that tells each investor what to report. Taxes: filed through CCH; K-1s are pulled from CCH and matched to investors, using the deal's closes up to the tax year.",
      },
      {
        type: "callout",
        tone: "warning",
        title: "Review before approving",
        text: "Once approved and paid, a wrong distribution means asking investors to send money back, and it flows into their K-1s. Check each investor's gross amount, carry and net before moving a distribution out of `pending_review`.",
      },
      {
        type: "paragraph",
        text: "Full flows on the Distributions and Taxes pages.",
      },
    ],
  },
  // ---- Special cases ----
  {
    id: "special-fund-manager-change",
    group: "Special cases",
    title: "Fund manager change",
    blocks: [
      {
        type: "paragraph",
        text: "Investors put money in because they trusted a specific person to run the deal. When that person changes, the one deciding over their money, earning their fees and carry, and owing them a duty of care changes too. That is why the change is controlled and investors are told.",
      },
      {
        type: "paragraph",
        text: "Once a deal has left draft, the fund manager and advisor seats are locked. They change only through a role change request.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Reason",
            description:
              "Restructuring, change of control, regulatory, resignation, migration, other.",
          },
          {
            term: "Effective date",
            description: "When the new holder takes the seat.",
          },
        ],
      },
      {
        type: "flow",
        steps: [
          "The fund manager files the request in Hercules with a reason and an effective date. They can withdraw it while pending.",
          "Ops approve or reject in Poseidon.",
          "Approved requests apply on the effective date, or wait (`awaiting_action`) on KYC/KYB of the new holder.",
        ],
      },
      {
        type: "callout",
        tone: "warning",
        title: "Investors must be told",
        text: "A fund manager change on a deal with active investors requires an investor notice, sent from Hercules. Changing who controls investors' money without telling them is a compliance failure.",
      },
      {
        type: "paragraph",
        text: "Ops can also assign a seat directly.",
      },
    ],
  },
  {
    id: "special-migrations",
    group: "Special cases",
    title: "Migrated deals",
    blocks: [
      {
        type: "paragraph",
        text: "Deals closed outside the platform and recorded in it afterwards. The money has already moved and the investors already own their stake; bringing the deal in gives them the same capital accounts, valuations, distributions and tax documents as any other deal. The job is to record what happened, not to raise again.",
      },
      {
        type: "list",
        items: [
          "Created as offline deals with `is_migration` = true: no data room, no emails, no reminders, just a Slack alert. Investors already invested, so they aren't solicited again.",
          "Shown with a red Migration tag.",
          "Amount changes on migrated investments apply directly, without approval.",
          "Migration fields (status, batch, approval date and email) are edited by ops in Poseidon.",
        ],
      },
      {
        type: "callout",
        tone: "warning",
        title: "No approval safety net",
        text: "Because amount changes on migrated investments apply directly, a typo goes straight into the investor's capital account, ownership and future distributions. Check amounts against the original records before saving.",
      },
    ],
  },
  {
    id: "special-test-deals",
    group: "Special cases",
    title: "Test deals",
    blocks: [
      {
        type: "paragraph",
        text: "Deals created to try the product, with no real money behind them. They must never be mistaken for a real raise: they would inflate the numbers reported on raised capital and deal count, and could reach real investors.",
      },
      {
        type: "list",
        items: [
          "A test deal has status test, or a test-like name (test, demo, dummy, sample, fake).",
          "Hercules refuses test-like names on live deals and points to demo.allocations.com.",
          "Poseidon only accepts a test-like name when the deal is created in test status.",
          "Test deals send no Slack alerts, stay out of the sales CRM and are left out of metrics.",
        ],
      },
    ],
  },
  {
    id: "special-archiving",
    group: "Special cases",
    title: "Archiving, cancelling, terminating",
    blocks: [
      {
        type: "paragraph",
        text: "Ways a deal stops. Archiving tidies away a draft that never raised money. Cancelling and terminating end a deal that went further, so what happens to any money already wired is the real question. Either way, a stopped deal must not take new money.",
      },
      {
        type: "list",
        items: [
          "Archive: the fund manager requests it for a draft deal only; ops approve in Poseidon.",
          "Archived and cancelled deals are hidden from anyone without a stake in them.",
          "Cancelled and terminated count as lost in the sales CRM, closed as won.",
          "Closed, archived, cancelled and terminated deals can't take new closes in Hercules.",
        ],
      },
    ],
  },
];
